import React, { useEffect } from 'react';
import nanoid from 'nanoid';
import classNames from 'classnames';

import Text from '../../../common/Text';
import { ClaimContext, OnChangeClaimForm, OrderItem } from '../types';
import PlainButton from '../../../common/PlainButton';
import AddIcon from '../../../../icons/add.svg';
import IconButton from '../../../common2/IconButton';
import Close from '../../../../icons/close.svg';
import { useNestedArrayError } from '../../../../hooks/useNestedArrayError';
import ValidationError from '../../../common/ValidationError';
import { isEmptyValidationResult } from '../../../../hooks/useYupValidation';
import Column from '../../../common/layout/Column';
import { BasicInput } from '../../../common2/BasicInput/BasicInput';
import Locale from '../../../Translate';

import styles from './OrderItemsInput.scss';

type OrderItemsInputProps = {
  onChange: OnChangeClaimForm;
  errors?: Record<string, string>;
  isDisabled?: boolean;
  isTaxRateShown: boolean;
} & Pick<ClaimContext, 'orderItems'>;

function createEmptyOrderItem(): OrderItem {
  return {
    unitPrice: '',
    quantity: '1',
    description: '',
    id: nanoid(),
  };
}

function getTotal(quantity: string | number | null, unitPrice: string | number | null): string {
  const total = Number(quantity) * Number(unitPrice);
  return isNaN(total) ? '0.00' : total.toFixed(2);
}

function OrderItemsInput(props: OrderItemsInputProps) {
  const { errors = {}, orderItems = [], onChange, isDisabled = false, isTaxRateShown } = props;

  const onAddButtonClick = () => onChange([...orderItems, createEmptyOrderItem()], 'orderItems');

  const onRemoveButtonClick = (removeIndex: number) => {
    const newData = orderItems.filter((_, index: number) => index !== removeIndex);
    onChange(newData, 'orderItems');
  };

  const onChangeOrderItem = (index: number, patch: Partial<OrderItem>) => {
    const newData = orderItems.slice();

    newData[index] = { ...newData[index], ...patch };

    onChange(newData, 'orderItems');
  };

  useEffect(() => {
    if (orderItems.length === 0) {
      onAddButtonClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderItemsError = useNestedArrayError('orderItems', errors, orderItems);

  return (
    <div className={styles.list} aria-label="Order items list">
      {orderItems.length > 0 && (
        <div className={classNames(styles.orderItemRow, { [styles['with-tax-rate']]: isTaxRateShown })}>
          <Text ink>
            <Locale>Item name</Locale>
          </Text>
          <Text ink>
            <Locale>Price</Locale>
          </Text>
          <Text ink>
            <Locale>Amount</Locale>
          </Text>
          {isTaxRateShown && (
            <Text ink>
              <Locale>Tax rate</Locale>
            </Text>
          )}
          {!isTaxRateShown && (
            <Text ink>
              <Locale>Total</Locale>
            </Text>
          )}
          <div />
        </div>
      )}
      {orderItems &&
        orderItems.map((item, index) => (
          <div key={index} className={classNames(styles.orderItemRow, { [styles['with-tax-rate']]: isTaxRateShown })}>
            <BasicInput
              key={`${index}-description`}
              name="description"
              placeholder="Item description..."
              required={true}
              value={item.description}
              isInvalid={!!orderItemsError[item.id]?.description}
              onChange={(data) => onChangeOrderItem(index, { description: data })}
              disabled={isDisabled}
            />
            <BasicInput
              key={`${index}-price`}
              name="unitPrice"
              placeholder="Price"
              required={true}
              value={item.unitPrice}
              isInvalid={!!orderItemsError[item.id]?.unitPrice}
              onChange={(data) => onChangeOrderItem(index, { unitPrice: data })}
              disabled={isDisabled}
            />
            <BasicInput
              key={`${index}-quantity`}
              name="quantity"
              placeholder="Quantity"
              required={true}
              value={item.quantity}
              isInvalid={!!orderItemsError[item.id]?.quantity}
              onChange={(data) => onChangeOrderItem(index, { quantity: data })}
              disabled={isDisabled}
            />
            {!isTaxRateShown && <Text ink>{getTotal(item.quantity, item.unitPrice)}</Text>}
            {isTaxRateShown && (
              <BasicInput
                key={`${index}-tax-rate`}
                name="taxRate"
                placeholder="0.0"
                required={true}
                value={item.taxRate}
                isInvalid={!!orderItemsError[item.id]?.taxRate}
                onChange={(data) => onChangeOrderItem(index, { taxRate: data })}
                rightElement="%"
                disabled={isDisabled}
              />
            )}
            <IconButton
              icon={Close}
              onClick={() => onRemoveButtonClick(index)}
              ariaLabel="Delete item"
              disabled={isDisabled}
            />
          </div>
        ))}
      <Column spacing="small">
        {(!isEmptyValidationResult(orderItemsError) || errors.orderItems) && (
          <ValidationError>
            <Locale>Missing or invalid order item information</Locale>
          </ValidationError>
        )}
        <PlainButton icon={AddIcon} onClick={onAddButtonClick} disabled={isDisabled}>
          Add item
        </PlainButton>
      </Column>
    </div>
  );
}

export default React.memo(OrderItemsInput);
